import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import {
  AlertController,
  ModalController,
  ModalOptions,
  NavController,
  NavParams,
} from "ionic-angular";
import moment from "moment";
import { BillingService } from "providers/billing/billing-service";
import { commonVar } from "../../../../providers/common-var/common";
import { UtilityService } from "../../../../providers/utility-service/utility-service";

@Component({
  selector: "claim-options",
  templateUrl: "claim-options.html",
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ClaimOptions implements OnInit, OnDestroy {
  cardHeader: string = "Billing Options";

  config: ClaimOptionsConfiguration = null;
  claimData: any;
  claimOptions: FormGroup;
  fromDate: any = null;
  datePlacementPreference: PlacementArray = ["bottom-left"];
  fieldNames = new Map();

  // Field mapping for history log display
  private static fieldDisplayNames = new Map<string, string>([
    // UB04 Fields
    ["otherID1", "FL57 Qualifier"],
    ["otherID2", "FL57 Other Provider ID"],
    ["remarks", "FL80"],
    ["bicc1", "FL81CCa 01"],
    ["bicc2", "FL81CCa 02"],
    ["bicc3", "FL81CCa 03"],
    ["bicc4", "FL81CCb 01"],
    ["bicc5", "FL81CCb 02"],
    ["bicc6", "FL81CCb 03"],
    ["bicc7", "FL81CCc 01"],
    ["bicc8", "FL81CCc 02"],
    ["bicc9", "FL81CCc 03"],
    ["bicc10", "FL81CCd 01"],
    ["bicc11", "FL81CCd 02"],
    ["bicc12", "FL81CCd 03"],

    // CMS1500 Fields
    ["physician number 17a1", "FL17a Qualifier"],
    ["physician number 17a", "FL17a Other Provider ID"],
    ["place", "FL24b"],
    ["emg", "FL24i"],
    ["local", "FL24j"],
    ["grp1", "FL32b"],
    ["pin", "FL33b Qualifier"],
    ["grp", "FL33b Billing Provider Other ID"],

    // Value Codes
    ["vc1", "FL39a Code"],
    ["vcAmount1", "FL39a Amount"],
    ["vc2", "FL39b Code"],
    ["vcAmount2", "FL39b Amount"],
    ["vc3", "FL39c Code"],
    ["vcAmount3", "FL39c Amount"],
    ["vc4", "FL39d Code"],
    ["vcAmount4", "FL39d Amount"],
    ["vc5", "FL40a Code"],
    ["vcAmount5", "FL40a Amount"],
    ["vc6", "FL40b Code"],
    ["vcAmount6", "FL40b Amount"],
    ["vc7", "FL40c Code"],
    ["vcAmount7", "FL40c Amount"],
    ["vc8", "FL40d Code"],
    ["vcAmount8", "FL40d Amount"],
    ["vc9", "FL41a Code"],
    ["vcAmount9", "FL41a Amount"],
    ["vc10", "FL41b Code"],
    ["vcAmount10", "FL41b Amount"],
    ["vc11", "FL41c Code"],
    ["vcAmount11", "FL41c Amount"],
    ["vc12", "FL41d Code"],
    ["vcAmount12", "FL41d Amount"],

    // Single Value Codes
    ["planID1", "FL51"],
    ["infoRel1", "FL52"],
    ["aBen1", "FL53"],

    // Date Fields
    ["dischargeStatus", "FL17"],
    ["CCRC1", "FL18"],
    ["CCRC2", "FL19"],
    ["CCRC3", "FL20"],
    ["CCRC4", "FL21"],
    ["CCRC5", "FL22"],
    ["CCRC6", "FL23"],
    ["CCRC7", "FL24"],
    ["CCRC8", "FL25"],
    ["CCRC9", "FL26"],
    ["CCRC10", "FL27"],
    ["CCRC11", "FL28"],
    ["OC1", "FL31a Code"],
    ["OC1Date", "FL31a Date"],
    ["OC2", "FL32a Code"],
    ["OC2Date", "FL32a Date"],
    ["OC3", "FL33a Code"],
    ["OC3Date", "FL33a Date"],
    ["OC4", "FL34a Code"],
    ["OC4Date", "FL34a Date"],
    ["OC5", "FL31b Code"],
    ["OC5Date", "FL31b Date"],
    ["OC6", "FL32b Code"],
    ["OC6Date", "FL32b Date"],
    ["OC7", "FL33b Code"],
    ["OC7Date", "FL33b Date"],
    ["OC8", "FL34b Code"],
    ["OC8Date", "FL34b Date"],
    ["OCSpan1", "FL35a Code "],
    ["OCSpan1Start", "FL35a From "],
    ["OCSpan1End", "FL35a Through "],
    ["OCSpan2", "FL36a Code"],
    ["OCSpan2Start", "FL36a From "],
    ["OCSpan2End", "FL36a Through "],
    ["OCSpan3", "FL35b Code"],
    ["OCSpan3Start", "FL35b From "],
    ["OCSpan3End", "FL35b Through "],
    ["OCSpan4", "FL36b Code"],
    ["OCSpan4Start", "FL36b From "],
    ["OCSpan4End", "FL36b Through "],
  ]);

  getFieldDisplayName(fieldName: string): string {
    return ClaimOptions.fieldDisplayNames.get(fieldName) || fieldName;
  }

  static getFieldDisplayName(fieldName: string): string {
    return ClaimOptions.fieldDisplayNames.get(fieldName) || fieldName;
  }

  dateFormat: string = "MMDDYY";
  datePickerFormat: string = "YYYY-MM-DD";
  saveEnabled = false;
  dateRegex = new RegExp("\\d{6}");
  numericMask = commonVar.SIMPLE_PHONE_MASK;

  FL17_SEARCH_LIST: CodeValues[] = [];
  FL18_28SEARCH_LIST: CodeValues[] = [];
  FL31_34SEARCH_LIST: CodeValues[] = [];
  FL35_36SEARCH_LIST: CodeValues[] = [];

  FL52SELECT = [
    {
      description: "Y: Yes, a signed statement is on file.",
      code: "Y",
    },
    {
      description:
        "I: Informed consent to release information has been obtained.",
      code: "I",
    },
  ];

  FL53SELECT = [
    {
      description: "Y: Yes, a signed assignment of benefits form is on file.",
      code: "Y",
    },
    {
      description:
        "N: No, a signed assignment of benefits form is not on file.",
      code: "N",
    },
    {
      description:
        "W: Not Applicable (assignment does not apply to this claim).",
      code: "W",
    },
  ];

  FL57SELECT = [
    {
      description: "G2 - Provider Number",
      code: "G2",
    },
    {
      description: "0B - State License Number",
      code: "0B",
    },
    {
      description: "LU - Location Number",
      code: "LU",
    },
    {
      description: "1G - Provider UPIN",
      code: "1G",
    },
  ];

  FL17A_QUALIFIER_OPTIONS = [];
  PLACE_OF_SERVICE_OPTIONS = [];
  FL24I_QUALIFIER_OPTIONS = [];
  FL33B_QUALIFIER_OPTIONS = [];

  constructor(
    protected navParams: NavParams,
    protected navCtrl: NavController,
    private fb: FormBuilder,
    private billingService: BillingService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private utilityService: UtilityService
  ) {
    this.claimData = navParams.data.claim;
    this.initConfiguration();
    this.initForm();
    let cardHeader = " - ";
    cardHeader += this.claimData.patient
      ? this.claimData.patient.patientFirstName +
        " " +
        this.claimData.patient.patientLastName +
        ", "
      : "";
    cardHeader += "Payer Name: " + this.claimData.payerName + ", ";
    cardHeader += "Claim ID: " + this.claimData.claimIdString + ", ";
    cardHeader += "Service Dates: " + this.claimData.serviceStartDate;
    if (this.claimData.serviceEndDate) {
      cardHeader += " - " + this.claimData.serviceEndDate;
    }
    this.cardHeader += cardHeader;
  }

  ngOnInit() {
    this.mapFormData();
    this.setupFormChangeDetection();
    this.saveEnabled = false;
  }

  ngOnDestroy() {}

  loadCMS1500Codes() {
    this.billingService.getCMS1500Codes().subscribe(
      (response: any) => {
        if (response.qualifierCodesFL17a) {
          this.FL17A_QUALIFIER_OPTIONS = [];
          response.qualifierCodesFL17a.forEach((value) => {
            this.FL17A_QUALIFIER_OPTIONS.push({
              description: value["code"] + " " + value["description"],
              code: value["code"],
            });
          });
        }

        if (response.qualifierCodesFL24i) {
          this.FL24I_QUALIFIER_OPTIONS = [];
          response.qualifierCodesFL24i.forEach((value) => {
            this.FL24I_QUALIFIER_OPTIONS.push({
              description: value["code"] + " " + value["description"],
              code: value["code"],
            });
          });
        }

        if (response.qualifierCodesFL17a) {
          this.FL33B_QUALIFIER_OPTIONS = [];
          response.qualifierCodesFL17a.forEach((value) => {
            this.FL33B_QUALIFIER_OPTIONS.push({
              description: value["code"] + " " + value["description"],
              code: value["code"],
            });
          });
        }

        if (response.placeOfServiceCodes) {
          this.PLACE_OF_SERVICE_OPTIONS = [];
          response.placeOfServiceCodes.forEach((value) => {
            this.PLACE_OF_SERVICE_OPTIONS.push({
              description: value["code"] + " " + value["description"],
              code: value["code"],
            });
          });
        }

        console.log("CMS-1500 codes loaded successfully");
      },
      (error) => {
        console.error("Error loading CMS-1500 codes:", error);
      }
    );
  }

  initCMS1500Config() {
    let fl17aConfig: GeneralCodesConfig = {
      title: "FL 17a",
      generalCodeList: [
        {
          name: "FL17a_qualifier",
          display: "Qualifier",
          type: "SEARCH",
          fieldName: "physician number 17a1",
        },
        {
          name: "FL17a_otherProviderId",
          display: "Other Provider ID",
          type: "TEXT",
          fieldName: "physician number 17a",
        },
      ],
    };
    this.config.generalCodes.push(fl17aConfig);

    let fl24Config: GeneralCodesConfig = {
      title: "FL 24",
      generalCodeList: [
        {
          name: "FL24b",
          display: " FL 24b Place of Service",
          type: "SEARCH",
          fieldName: "place",
        },
        {
          name: "FL24i_qualifier",
          display: "FL 24i Qualifier",
          type: "SEARCH",
          fieldName: "emg",
        },
        {
          name: "FL24i_otherProviderId",
          display: "FL 24j Other Provider ID",
          type: "TEXT",
          fieldName: "local",
        },
      ],
    };
    this.config.generalCodes.push(fl24Config);

    let fl32bConfig: GeneralCodesConfig = {
      title: "FL 32b",
      generalCodeList: [
        {
          name: "FL32b",
          display: "Other ID",
          type: "TEXT",
          fieldName: "grp1",
        },
      ],
    };
    this.config.generalCodes.push(fl32bConfig);

    let fl33bConfig: GeneralCodesConfig = {
      title: "FL 33b",
      generalCodeList: [
        {
          name: "FL33b_qualifier",
          display: "Qualifier",
          type: "SEARCH",
          fieldName: "pin",
        },
        {
          name: "FL33b_billingProviderOtherId",
          display: "Billing Provider Other ID",
          type: "TEXT",
          fieldName: "grp",
        },
      ],
    };
    this.config.generalCodes.push(fl33bConfig);
  }

  initConfiguration() {
    //Load fl17 config
    this.FL17_SEARCH_LIST = [];
    this.FL18_28SEARCH_LIST = [];
    this.config = {
      generalCodes: [],
      valueCodes: [],
      singleValueCodes: [],
      qualifier: null,
      remarks: null,
      ccFields: null,
    };

    this.config.generalCodes = [];

    if (this.isProfessionalClaimType()) {
      this.loadCMS1500Codes();
      this.initCMS1500Config();
      return;
    }

    this.loadSearchLists();
    let fl17Config: GeneralCodesConfig = {
      title: "",
      generalCodeList: [],
    };
    let fl17GC: GeneralCode = {
      name: "FL17",
      display: "FL 17 Patient Status",
      type: "SEARCH",
      fieldName: "dischargeStatus",
    };

    fl17Config.generalCodeList.push(fl17GC);

    this.config.generalCodes.push(fl17Config);

    //Load fl18-28 config

    let fl18_28Config: GeneralCodesConfig = {
      title: "Condition Codes",
      generalCodeList: [],
    };

    for (let i = 18; i < 29; i++) {
      let flGC: GeneralCode = {
        name: "FL" + i,
        display: "FL " + i,
        type: "SEARCH",
        fieldName: "CCRC" + (i - 17),
      };
      fl18_28Config.generalCodeList.push(flGC);
    }

    this.config.generalCodes.push(fl18_28Config);

    //Load fl31-34 config
    let fl31_34Config: GeneralCodesConfig = {
      title: "Occurrence Codes",
      generalCodeList: [],
    };
    for (let i = 31; i < 35; i++) {
      let flGC: GeneralCode = {
        name: "FL" + i,
        display: "FL " + i,
        type: "DATE",
        fieldName: "OC",
      };
      fl31_34Config.generalCodeList.push(flGC);
    }
    this.config.generalCodes.push(fl31_34Config);

    //Load fl35-36 config
    let fl35_36Config: GeneralCodesConfig = {
      title: "Occurrence Span Codes",
      generalCodeList: [
        {
          name: "FL35",
          display: "FL 35",
          type: "DATERANGE",
          fieldName: "OCSpan",
        },
        {
          name: "FL36",
          display: "FL 36",
          type: "DATERANGE",
          fieldName: "OCSpan",
        },
      ],
    };
    this.config.generalCodes.push(fl35_36Config);

    //Load fl39-41 config
    this.config.valueCodes = [
      {
        name: "FL39",
        display: "FL 39",
        type: "VALUECODE",
        fieldName: "vc",
      },
      {
        name: "FL40",
        display: "FL 40",
        type: "VALUECODE",
        fieldName: "vc",
      },
      {
        name: "FL41",
        display: "FL 41",
        type: "VALUECODE",
        fieldName: "vc",
      },
    ];

    //Load fl51-53 config
    this.config.singleValueCodes = [
      {
        name: "FL51",
        display: "FL 51",
        type: "VALUECODE",
        fieldName: "planID1",
      },
      {
        name: "FL52",
        display: "FL 52",
        type: "VALUECODE",
        fieldName: "infoRel1",
      },
      {
        name: "FL53",
        display: "FL 53",
        type: "VALUECODE",
        fieldName: "aBen1",
      },
    ];

    //Load fl57-81 config
    this.config.qualifier = {
      name: "FL57",
      display: "FL 57",
      type: "Qualifier",
      fieldName: "otherID",
    };

    this.config.remarks = {
      name: "FL80",
      display: "FL 80",
      type: "Remarks",
      fieldName: "remarks",
    };

    this.config.remarks = {
      name: "FL81",
      display: "FL 81CC",
      type: "CCFIELDS",
      fieldName: "bicc",
    };
  }

  initForm() {
    this.claimOptions = new FormGroup({});

    if (this.isProfessionalClaimType()) {
      for (let generalCode of this.config.generalCodes) {
        for (let code of generalCode.generalCodeList) {
          this.claimOptions.addControl(code.name, new FormControl(""));
          this.fieldNames.set(code.fieldName, code.name);
        }
      }
      this.setupCMS1500QualifierSubscriptions();
      return;
    }

    //loading 17-34
    let datecounter = 1;
    let rangeCounter = 1;
    for (let generalCodesConfig of this.config.generalCodes) {
      for (let code of generalCodesConfig.generalCodeList) {
        if (code.type === "DATE") {
          this.claimOptions.addControl(
            code.name,
            this.fb.group({
              searchValueA: ["", Validators.required],
              searchValueB: ["", Validators.required],
              dateA: ["", Validators.required],
              dateB: ["", Validators.required],
            })
          );
          this.fieldNames.set(
            code.fieldName + datecounter,
            code.name + "." + "searchValueA"
          );
          this.fieldNames.set(
            code.fieldName + datecounter + "Date",
            code.name + "." + "dateA"
          );
          this.fieldNames.set(
            code.fieldName + (datecounter + 4),
            code.name + "." + "searchValueB"
          );
          this.fieldNames.set(
            code.fieldName + (datecounter + 4) + "Date",
            code.name + "." + "dateB"
          );
          datecounter++;
        } else if (code.type === "DATERANGE") {
          this.claimOptions.addControl(
            code.name,
            this.fb.group({
              searchValueA: ["", Validators.required],
              searchValueB: ["", Validators.required],
              fromA: ["", Validators.required],
              fromB: ["", Validators.required],
              throughA: ["", Validators.required],
              throughB: ["", Validators.required],
            })
          );
          this.fieldNames.set(
            code.fieldName + rangeCounter,
            code.name + "." + "searchValueA"
          );
          this.fieldNames.set(
            code.fieldName + rangeCounter + "Start",
            code.name + "." + "fromA"
          );
          this.fieldNames.set(
            code.fieldName + rangeCounter + "End",
            code.name + "." + "throughA"
          );

          this.fieldNames.set(
            code.fieldName + (rangeCounter + 2),
            code.name + "." + "searchValueB"
          );
          this.fieldNames.set(
            code.fieldName + (rangeCounter + 2) + "Start",
            code.name + "." + "fromB"
          );
          this.fieldNames.set(
            code.fieldName + (rangeCounter + 2) + "End",
            code.name + "." + "throughB"
          );

          rangeCounter++;
        } else {
          this.claimOptions.addControl(code.name, new FormControl(""));
          this.fieldNames.set(code.fieldName, code.name);
        }
      }
    }
    //loading 39-41
    let indexCounter = 1;
    for (let code of this.config.valueCodes) {
      this.claimOptions.addControl(code.name, this.fb.group({}));
      for (let i = 1; i < 5; i++) {
        (<FormGroup>this.claimOptions.get(code.name)).addControl(
          "code" + i,
          new FormControl("")
        );
        this.fieldNames.set(
          code.fieldName + indexCounter,
          code.name + "." + "code" + i
        );
        (<FormGroup>this.claimOptions.get(code.name)).addControl(
          "amount" + i,
          new FormControl("")
        );
        this.fieldNames.set(
          code.fieldName + "Amount" + indexCounter,
          code.name + "." + "amount" + i
        );
        indexCounter++;
        (<FormGroup>this.claimOptions.get(code.name)).addControl(
          "decimal" + i,
          new FormControl("")
        );
      }
    }
    //loading 51-53
    for (let code of this.config.singleValueCodes) {
      this.claimOptions.addControl(code.name, new FormControl(""));
      this.fieldNames.set(code.fieldName, code.name);
    }
    //loading 57
    this.claimOptions.addControl(
      "FL57",
      this.fb.group({
        qualifier: ["", Validators.required],
        providerId: ["", Validators.required],
      })
    );
    this.fieldNames.set("otherID1", "FL57.qualifier");
    this.fieldNames.set("otherID2", "FL57.providerId");

    //loading 80
    this.claimOptions.addControl("FL80", new FormControl(""));
    this.fieldNames.set("remarks", "FL80");

    //loading 81
    this.claimOptions.addControl("FL81", this.fb.group({}));
    let counterBCC = 1;
    for (let i of ["a", "b", "c", "d"]) {
      (<FormGroup>this.claimOptions.get("FL81")).addControl(
        "first" + i,
        new FormControl("")
      );
      this.fieldNames.set("bicc" + counterBCC, "FL81.first" + i);
      counterBCC++;
      (<FormGroup>this.claimOptions.get("FL81")).addControl(
        "second" + i,
        new FormControl("")
      );
      this.fieldNames.set("bicc" + counterBCC, "FL81.second" + i);
      counterBCC++;
      (<FormGroup>this.claimOptions.get("FL81")).addControl(
        "third" + i,
        new FormControl("")
      );
      this.fieldNames.set("bicc" + counterBCC, "FL81.third" + i);
      counterBCC++;
    }
  }

  mapFormData() {
    let isClaimOption = Array.isArray(this.claimData.claimOptions);
    if (this.claimData.claimOptions) {
      for (var key in this.claimData.claimOptions) {
        let controlName = null;
        if (isClaimOption)
          controlName = this.fieldNames.get(
            this.claimData.claimOptions[key].fieldName
          );
        else {
          controlName = this.fieldNames.get(key);
        }
        if (controlName == null) continue;
        let value = null;
        value = this.claimData.claimOptions[key];

        if (this.isProfessionalClaimType()) {
          let control = this.claimOptions.get(controlName);
          if (control == null) continue;
          control.setValue(value.fieldValue);
        } else {
          controlName = controlName.split(".");
          let control = this.claimOptions.get(controlName[0]);
          if (control == null) continue;

          if (controlName.length > 1) {
            if (
              controlName[1].includes("date") ||
              controlName[1].includes("from") ||
              controlName[1].includes("through")
            ) {
              let date = "";
              if (this.dateRegex.test(value.fieldValue))
                date = moment(value.fieldValue, this.dateFormat).format(
                  this.datePickerFormat
                );
              control.get(controlName[1]).setValue(date);
            } else {
              control.get(controlName[1]).setValue(value.fieldValue);
            }
          } else control.setValue(value.fieldValue);
        }
      }
    }
  }

  getOptionsForCMS1500Field(fieldName: string) {
    if (
      fieldName.includes("FL17a_qualifier") ||
      fieldName.includes("FL33b_qualifier")
    ) {
      return this.FL17A_QUALIFIER_OPTIONS;
    }
    if (fieldName.includes("FL24i_qualifier")) {
      return this.FL24I_QUALIFIER_OPTIONS;
    }
    if (fieldName.includes("FL24b")) {
      return this.PLACE_OF_SERVICE_OPTIONS;
    }
    return [];
  }

  loadSearchLists() {
    let isCorrection = this.claimData.originalClaimId != null;
    this.billingService.getPatientStatus().subscribe((x) => {
      this.processResponseToList(x, "FL17_SEARCH_LIST");
    });

    this.billingService.getConditionCodes(isCorrection).subscribe((x) => {
      this.processResponseToList(x, "FL18_28SEARCH_LIST");
    });

    this.billingService.getOccurrenceCodes().subscribe((x) => {
      this.processResponseToList(x, "FL31_34SEARCH_LIST");
    });

    this.billingService.getOccurrenceSpan().subscribe((x) => {
      this.processResponseToList(x, "FL35_36SEARCH_LIST");
    });
  }

  processResponseToList(response, list) {
    response.forEach((value) => {
      let val: CodeValues = {
        description: value["code"] + " " + value["description"],
        code: value["code"],
      };
      this[list] = [...this[list], val];
    });
  }
  getRemarksLength() {
    let control = this.claimOptions.get("FL80");
    if (control == null) return 0;
    return control.value.length;
  }

  getSelectValue(controlName, list, field) {
    let control = this.claimOptions.get(controlName);
    if (control == null) return "";
    let result;
    if (field) result = list.find((f) => f.code == control.get(field).value);
    else result = list.find((f) => f.code == control.value);
    if (result == null) return "";
    return result.description;
  }

  isProviderDisabled(providerControlName: string): boolean {
    if (!this.isProfessionalClaimType()) return false;
    // map provider field to its qualifier
    const map: any = {
      FL17a_otherProviderId: "FL17a_qualifier",
      FL24i_otherProviderId: "FL24i_qualifier",
      FL33b_billingProviderOtherId: "FL33b_qualifier",
    };
    const qualifierName = map[providerControlName];
    if (!qualifierName) return false;
    const qualifier = this.claimOptions.get(qualifierName);
    return !qualifier || !qualifier.value || qualifier.value.length === 0;
  }

  controlHasValue(controlName, field) {
    let control = this.claimOptions.get(controlName);
    if (control == null) return false;
    if (
      control.get(field).value != null &&
      control.get(field).value.length == 0
    )
      return false;
    if (control.get(field).value == null) return false;
    return control.get(field).value;
  }

  limitDateControl(controlName, field, modifier) {
    let control = this.claimOptions.get(controlName);
    if (control == null) return null;
    let minDate = moment(control.get(field).value);
    let result: any = {
      year: minDate.year(),
      month: minDate.month() + 1,
      day: minDate.date() + modifier,
    };
    return result;
  }

  clearedValue(event, controlName, fieldName, clearName) {
    let value = this.controlHasValue(controlName, fieldName);
    if (value != false) {
      return;
    }

    let fields = clearName.split(",");
    if (fields.length > 1) {
      let control = this.claimOptions.get(controlName);
      control.get(fields[0]).setValue("");
      control.get(fields[1]).setValue("");
    } else {
      let control = this.claimOptions.get(controlName);
      control.get(clearName).setValue("");
    }
  }

  reloadClaimData() {
    const deleteModalOptions: ModalOptions = {
      enableBackdropDismiss: false,
      cssClass: "exit-edit-claim-confirmation-popup",
    };

    let modalPage = this.modalCtrl.create(
      "ExitEditClaimPage",
      {
        title: "Reload Claim Data",
      },
      deleteModalOptions
    );
    modalPage.onDidDismiss((data) => {
      if (data.proceed) {
        //Call Endpoint here
        this.billingService
          .reloadClaimData(this.claimData.claimId)
          .subscribe((x) => {
            if (this.navParams.data.parent) {
              this.navParams.data.parent.billingService.getClaims(1, 10);
            }
            this.navCtrl.pop();
          });
      }
    });
    modalPage.present();
  }

  validateFields() {
    if (this.isProfessionalClaimType()) {
      const fl24b = this.claimOptions.get("FL24b");
      if (!fl24b || !fl24b.value || (fl24b.value + "").length === 0) {
        return false;
      }

      const pairs: Array<[string, string]> = [
        ["FL17a_qualifier", "FL17a_otherProviderId"],
        ["FL24i_qualifier", "FL24i_otherProviderId"],
        ["FL33b_qualifier", "FL33b_billingProviderOtherId"],
      ];
      for (const [qualName, idName] of pairs) {
        const qualCtrl = this.claimOptions.get(qualName);
        const idCtrl = this.claimOptions.get(idName);
        if (!qualCtrl || !idCtrl) continue;
        const q = (qualCtrl.value || "").toString();
        const p = (idCtrl.value || "").toString();
        if (
          (q.length > 0 && p.length === 0) ||
          (p.length > 0 && q.length === 0)
        ) {
          return false;
        }
      }
      return true;
    }
    for (let generalCodesConfig of this.config.generalCodes) {
      for (let code of generalCodesConfig.generalCodeList) {
        let control = this.claimOptions.get(code.name);
        if (code.type === "DATE") {
          if (
            (control.get("searchValueA").value == null ||
              control.get("searchValueA").value.length == 0) &&
            (control.get("dateA").value == null ||
              control.get("dateA").value.length == 0) &&
            (control.get("searchValueB").value == null ||
              control.get("searchValueB").value.length == 0) &&
            (control.get("dateB").value == null ||
              control.get("dateB").value.length == 0)
          )
            continue;
          if (
            (control.get("searchValueA").value != null &&
              control.get("searchValueA").value.length > 0 &&
              (control.get("dateA").value == null ||
                control.get("dateA").value.length == 0)) ||
            (control.get("searchValueB").value != null &&
              control.get("searchValueB").value.length > 0 &&
              (control.get("dateB").value == null ||
                control.get("dateB").value.length == 0)) ||
            (((control.get("searchValueB").value != null &&
              control.get("searchValueB").value.length > 0) ||
              (control.get("dateB").value != null &&
                control.get("dateB").value.length > 0)) &&
              (control.get("searchValueA").value == null ||
                control.get("searchValueA").value.length == 0 ||
                control.get("dateA").value == null ||
                control.get("dateA").value.length == 0))
          )
            return false;
        } else if (code.type === "DATERANGE") {
          if (
            (control.get("searchValueA").value != null &&
              control.get("searchValueA").value.length > 0 &&
              (control.get("fromA").value == null ||
                control.get("throughA").value == null ||
                control.get("fromA").value.length == 0 ||
                control.get("throughA").value.length == 0)) ||
            (control.get("searchValueB").value != null &&
              control.get("searchValueB").value.length > 0 &&
              (control.get("fromB").value == null ||
                control.get("throughB").value == null ||
                control.get("fromB").value.length == 0 ||
                control.get("throughB").value.length == 0)) ||
            (((control.get("searchValueB").value != null &&
              control.get("searchValueB").value.length > 0) ||
              (control.get("fromB").value != null &&
                control.get("fromB").value.length > 0) ||
              (control.get("throughB").value != null &&
                control.get("throughB").value.length > 0)) &&
              ((control.get("searchValueA").value == null &&
                control.get("searchValueA").value.length == 0) ||
                (control.get("fromA").value == null &&
                  control.get("fromA").value.length == 0) ||
                (control.get("throughA").value == null &&
                  control.get("throughA").value.length == 0)))
          )
            return false;
        }
      }
    }
    for (let code of this.config.valueCodes) {
      let control = this.claimOptions.get(code.name);
      if (control == null) continue;
      for (let i = 1; i < 5; i++) {
        if (
          (control.get("code" + i).value != null &&
            control.get("code" + i).value.length > 0 &&
            control.get("amount" + i).value != null &&
            control.get("amount" + i).value.replace(/[^0-9]/g, "").length ==
              0) ||
          (control.get("amount" + i).value != null &&
            control.get("amount" + i).value.replace(/[^0-9]/g, "").length > 0 &&
            (control.get("code" + i).value == null ||
              control.get("code" + i).value.length == 0))
        )
          return false;
      }
    }
    return true;
  }

  handleCancelClose() {
    if (this.compareChanges()) {
      let alert = this.alertCtrl.create({
        //discard changes pop-up message to let user any unsaved changes will be discarded would they like to continue
        title: "Unsaved changes",
        message:
          "Any unsaved changes will be discarded, would you like to continue?",
        buttons: [
          {
            text: "Yes",
            handler: () => {
              this.close();
            },
          },
          {
            text: "No",
          },
        ],
      });
      alert.present();
    } else {
      this.close();
    }
  }
  compareChanges() {
    let body = this.createBody();
    let isClaimOption = Array.isArray(this.claimData.claimOptions);
    if (this.claimData.claimOptions) {
      if (
        body.claimOptions.length !=
        Object.keys(this.claimData.claimOptions).length
      ) {
        return true;
      }
      for (let co of body.claimOptions) {
        let current = null;
        if (isClaimOption) {
          current = this.claimData.claimOptions.find(
            (o) => o.fieldName == co.fieldName
          );
        } else {
          current = this.claimData.claimOptions[co.fieldName];
        }

        if (current && !this.compareFieldValues(current, co)) return true;
      }
      return false;
    } else if (body.claimOptions.length == 0) {
      return false;
    }
    return true;
  }

  compareFieldValues(current, saved) {
    if (current == null || saved == null) return false;
    return current.fieldValue == saved.fieldValue;
  }

  close() {
    this.utilityService.increaseSpinnerCount("Loading.");
    setTimeout(() => {
      this.utilityService.decreaseSpinnerCount();
      if (this.navParams.data.parent) {
        this.navParams.data.parent.billingService.getClaims(1, 10);
      }
      this.navCtrl.pop();
    }, 500);
  }

  checkChar(event: any, type: any) {
    let newValue = event.target.value;
    if (newValue !== null && newValue !== "") {
      let regExp = null;
      switch (type) {
        case "alphaOnly":
          regExp = new RegExp("^[A-Za-z]+$");
          if (!regExp.test(newValue)) {
            event.target.value = newValue.replace(/[^A-Za-z]/g, "");
          }
          break;
        case "alphaNumericOnly":
          regExp = new RegExp("^[A-Za-z0-9]+$");
          if (!regExp.test(newValue)) {
            event.target.value = newValue.replace(/[^A-Za-z0-9]/g, "");
          }
          break;
        case "numericOnly":
          regExp = new RegExp("^[0-9]+$");
          if (!regExp.test(newValue)) {
            event.target.value = newValue.replace(/[^0-9]/g, "");
          }
          break;
      }
    }
  }

  createBody() {
    let body: ClaimOptionsRequest = {
      claimId: this.claimData.claimId,
      claimOptions: [],
    };

    let isClaimOption = Array.isArray(this.claimData.claimOptions);
    let mapIter = this.fieldNames.entries();
    let iterResult;
    let whileCounter = 0;

    do {
      iterResult = mapIter.next();
      whileCounter++;
      if (iterResult.value == null) {
        continue;
      }

      let controlName = this.isProfessionalClaimType()
        ? iterResult.value[1]
        : iterResult.value[1].split(".")[0];
      let control = this.claimOptions.get(controlName);

      if (control == null) {
        continue;
      }

      let val = null;
      if (this.isProfessionalClaimType()) {
        val = control.value;
      } else {
        let controlNameParts = iterResult.value[1].split(".");
        if (controlNameParts.length > 1) {
          let fieldName = controlNameParts[1];
          if (
            fieldName.includes("date") ||
            fieldName.includes("from") ||
            fieldName.includes("through")
          ) {
            val =
              control.get(fieldName).value != null &&
              control.get(fieldName).value.length > 0
                ? moment(control.get(fieldName).value).format(this.dateFormat)
                : "";
          } else {
            val = control.get(fieldName).value;
          }
        } else val = control.value;
      }

      if (val == null) {
        val = "";
      }

      let claimOptionData: ClaimOptionData = {
        fieldName: this.isProfessionalClaimType()
          ? iterResult.value[0]
          : iterResult.value[0],
        fieldValue: val,
        overridden: true,
      };
      if (this.claimData.claimOptions) {
        let found = null;
        if (isClaimOption) {
          found = this.claimData.claimOptions.find(
            (co) => co.fieldName == claimOptionData.fieldName
          );
        } else {
          found = this.claimData.claimOptions[claimOptionData.fieldName];
        }

        if (val.length == 0 && found == null) {
          continue;
        }
        if (this.compareFieldValues(claimOptionData, found)) {
          claimOptionData.overridden = found.overridden;
        }
      } else if (val.length == 0) {
        continue;
      }
      body.claimOptions.push(claimOptionData);
    } while (iterResult.done == false && whileCounter < 80);
    return body;
  }

  onSave() {
    if (!this.validateFields()) return;
    this.utilityService.increaseSpinnerCount("Loading.");
    let body = this.createBody();

    this.saveEnabled = false;
    this.billingService.saveClaimOptionsCodes(body).subscribe(() => {
      setTimeout(() => {
        this.utilityService.decreaseSpinnerCount();
        this.saveEnabled = true;
        this.showAlert(
          {
            title: "Success",
            message: "Claim options saved.",
          },
          () => {
            this.close();
          }
        );
        this.loadBodyToClaimData(body);
      }, 500);
    }, this.onSaveError);
  }

  loadBodyToClaimData(body: ClaimOptionsRequest) {
    let options = {};
    for (let value of body.claimOptions) {
      options[value.fieldName] = {
        fieldValue: value.fieldValue,
        overridden: value.overridden,
      };
    }
    this.claimData.claimOptions = options;
  }

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert(
          {
            title: "Error",
            message: error.error.message,
          },
          null
        );
      } else {
        this.showAlert(
          {
            title: "Error",
            message: "Error while saving claim options. " + error.message,
          },
          null
        );
      }
    }
  };

  showAlert = ({ title, message }, handler) => {
    let alert = this.alertCtrl.create({
      title,
      message,
      enableBackdropDismiss: false,
      buttons: [
        {
          text: "Ok",
          handler: handler,
        },
      ],
    });
    alert.present();
  };

  isProfessionalClaimType(): boolean {
    const pdfType = (this.claimData?.pdfType || "").toString().toUpperCase();
    const claimType = (this.claimData?.claimType || "")
      .toString()
      .toUpperCase();
    return (
      pdfType === "CMS-1500" &&
      claimType !== "NOE" &&
      claimType !== "NOC" &&
      claimType !== "NOTR"
    );
  }

  setupFormChangeDetection() {
    this.claimOptions.valueChanges.subscribe(() => {
      this.saveEnabled = true;
    });
  }

  private setupCMS1500QualifierSubscriptions(): void {
    const pairs: Array<[string, string]> = [
      ["FL17a_qualifier", "FL17a_otherProviderId"],
      ["FL24i_qualifier", "FL24i_otherProviderId"],
      ["FL33b_qualifier", "FL33b_billingProviderOtherId"],
    ];
    pairs.forEach(([qualName, idName]) => {
      const qualCtrl = this.claimOptions.get(qualName);
      const idCtrl = this.claimOptions.get(idName);
      if (!qualCtrl || !idCtrl) return;
      const applyState = () => {
        const q = (qualCtrl.value || "").toString();
        if (q.length === 0) {
          idCtrl.disable({ emitEvent: false });
          idCtrl.setValue("", { emitEvent: false });
          idCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        } else {
          idCtrl.enable({ emitEvent: false });
          idCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        }
      };
      applyState();
      qualCtrl.valueChanges.subscribe(applyState);
    });
  }
}

class GeneralCode {
  name: string;
  display: string;
  type: string;
  fieldName: string;
}

class GeneralCodesConfig {
  title: string;
  generalCodeList: GeneralCode[];
}

class ClaimOptionsConfiguration {
  generalCodes: GeneralCodesConfig[];
  valueCodes: GeneralCode[];
  singleValueCodes: GeneralCode[];
  qualifier: GeneralCode;
  remarks: GeneralCode;
  ccFields: GeneralCode;
}

class CodeValues {
  code: string;
  description: string;
}

class ClaimOptionData {
  fieldName: string;
  fieldValue: string;
  overridden: boolean;
}

class ClaimOptionsRequest {
  claimId: number;
  claimOptions: ClaimOptionData[];
}



// WEBPACK FOOTER //
// ./src/pages/billing/claim-management/claim-options/claim-options.ts